import {
  SearchCriteriaInterface,
  EmployeeRangeSearchInterface,
  InsightSearchInterface,
  CompanyNaicsSearchInterface,
} from 'appTypes';

const HEAP_IGNORED_CRITERIA = ['activityDateRange', 'searchOptions'];

/*
 * Handles the employee range search criteria for Heap tracking.
 * @param {string} key - The key of the search criteria.
 * @param {EmployeeRangeSearchInterface} value - The value of the search criteria.
 */
const handleEmployeeRangeSearch = (key: string, value: EmployeeRangeSearchInterface) => {
  if (value && typeof value === 'object' && 'employeeRange' in value) {
    const range = value.employeeRange;
    if (Array.isArray(range) && range[0] === 0 && range[1] === 10000) return;

    window.heap.track(`miedge-broker-search-${key}`, {
      employeeRangeSearchMin: range?.[0],
      employeeRangeSearchMax: range?.[1],
    });
  }
};

/*
 * Handles the insight search criteria for Heap tracking.
 * @param {string} key - The key of the search criteria.
 * @param {InsightSearchInterface} value - The value of the search criteria.
 */
const handleInsightSearch = (key: string, value: InsightSearchInterface) => {
  if (value && typeof value === 'object' && 'revenueRange' in value) {
    const { min, max } = value.revenueRange || {};
    if (typeof min === 'number' && typeof max === 'number') {
      window.heap.track(`miedge-broker-search-revenueRange`, {
        RevenueRangeMin: min,
        RevenueRangeMax: max,
      });
    }
  }
};

/*
 * Handles the company NAICS search criteria for Heap tracking.
 * @param {string} key - The key of the search criteria.
 * @param {CompanyNaicsSearchInterface} value - The value of the search criteria.
 */
const handleCompanyNaicsSearch = (key: string, value: CompanyNaicsSearchInterface) => {
  if (
    typeof value === 'object' &&
    value !== null &&
    'industry_search_criterias' in value &&
    Array.isArray(value.industry_search_criterias)
  ) {
    const codes = value.industry_search_criterias
      .map((item) => item.code)
      .filter(Boolean)
      .join(',');

    if (codes) {
      window.heap.track(`miedge-broker-search-${key}`, {
        companyNaicsCodes: codes,
      });
    }
  }
};

/*
 * Handles generic values for Heap tracking.
 * @param {any} key - The key of the search criteria.
 * @param {any} value - The value of the search criteria.
 */
const handleGenericValue = (key: any, value: any) => {
  if (value && (Array.isArray(value) ? value.length > 0 : true)) {
    let formattedValue;
    if (Array.isArray(value)) {
      formattedValue = value
        .map((item) => (typeof item === 'object' ? item.label || JSON.stringify(item) : item))
        .join(',');
    } else {
      formattedValue = value;
    }

    window.heap.track(`miedge-broker-search-${key}`, {
      [key]: formattedValue,
    });
  }
};

/*
 * Sends the broker search criteria to Heap for tracking.
 * @param {SearchCriteriaInterface} brokerSearchCriteria - The search criteria to be sent to Heap.
 */
export const trackBrokerSearchWithHeap = (brokerSearchCriteria: SearchCriteriaInterface) => {
  Object.entries(brokerSearchCriteria).forEach(([key, value]) => {
    if (HEAP_IGNORED_CRITERIA.includes(key)) return;

    switch (key) {
      case 'employeeRangeSearch':
        handleEmployeeRangeSearch(key, value);
        break;
      case 'insightSearch':
        handleInsightSearch(key, value);
        break;
      case 'companyNaicsSearch':
        handleCompanyNaicsSearch(key, value);
        break;
      default:
        handleGenericValue(key, value);
    }
  });
};

/*
 * Tracks a flat true/false for each broker search criteria, sending one event per search.
 * @param {SearchCriteriaInterface} brokerSearchCriteria - The search criteria to be sent to Heap.
 */
export const trackBrokerSearchCriteriaFlags = (brokerSearchCriteria: SearchCriteriaInterface) => {
  if (!window.heap?.track) return;

  const isNonEmptyArray = (arr: any) => Array.isArray(arr) && arr.length > 0;

  const lobKeys = [
    'lob_business_travel',
    'lob_critical_illness',
    'lob_dental',
    'lob_executive',
    'lob_health',
    'lob_indemnity',
    'lob_legal',
    'lob_life_insurance',
    'lob_long_term_care',
    'lob_long_term_disability',
    'lob_statutory_disability',
    'lob_stop_loss',
    'lob_supplemental_unemployment',
    'lob_temporary_disability',
    'lob_vision',
    'lob_voluntary_all',
    'lob_voluntary_life',
    'lob_voluntary_ltd',
    'lob_voluntary_std',
    'lob_wellness',
  ];

  const flags: Record<string, boolean | string> = {
    benefits_broker: !!brokerSearchCriteria.benefits_broker,
    benefits_broker_address: !!brokerSearchCriteria.benefits_broker_address,
    benefits_broker_city: !!brokerSearchCriteria.benefits_broker_city,
    benefits_broker_zip: !!brokerSearchCriteria.benefits_broker_zip,
    broker_states: isNonEmptyArray(brokerSearchCriteria.broker_states),
    benefits_broker_counties: isNonEmptyArray(brokerSearchCriteria.benefits_broker_counties),
    benefits_carrier: !!brokerSearchCriteria.benefits_carrier,
    benefits_carriers_normalized: isNonEmptyArray(
      brokerSearchCriteria.benefits_carriers_normalized
    ),
    broker_name_normalized: isNonEmptyArray(brokerSearchCriteria.broker_bins),
    company_states: isNonEmptyArray(brokerSearchCriteria.states),
    benefits_plan_renewal_months: isNonEmptyArray(
      brokerSearchCriteria.benefits_plan_renewal_months
    ),
    companyNaicsSearch: !!(
      brokerSearchCriteria.companyNaicsSearch?.industry_search_criterias &&
      isNonEmptyArray(brokerSearchCriteria.companyNaicsSearch.industry_search_criterias)
    ),
    employeeRangeSearch: !!(
      brokerSearchCriteria.employeeRangeSearch?.employeeRange &&
      brokerSearchCriteria.employeeRangeSearch.employeeRange.length === 2 &&
      (brokerSearchCriteria.employeeRangeSearch.employeeRange[0] !== 0 ||
        brokerSearchCriteria.employeeRangeSearch.employeeRange[1] !== 10000)
    ),
    revenueRange: !!(
      brokerSearchCriteria.insightSearch?.revenueRange?.min != null &&
      brokerSearchCriteria.insightSearch.revenueRange.max != null
    ),
    lines_of_business: lobKeys.some(
      (key) =>
        brokerSearchCriteria[key as keyof SearchCriteriaInterface] !== undefined &&
        brokerSearchCriteria[key as keyof SearchCriteriaInterface] !== ''
    ),
    line_of_business_freeform: !!brokerSearchCriteria.lob_free_form,
  };

  window.heap.track('miedge-broker-search-criteria-flags', flags);
};

/*
 * Identifies the user in Heap with their information.
 * @param {UserInfo} userInfo - The user information to be sent to Heap.
 */
export const heapIdentify = (userInfo: any) => {
  if (window.heap.identify && userInfo && !isZywaveUser(userInfo)) {
    window.heap.identify(userInfo.id);
    window.heap.addUserProperties({
      Email: userInfo.email,
      'Account Id': userInfo.account_id,
      'Account Type': userInfo.account_type,
      'User Name': userInfo.username,
    });
  }
};

/*
 * Checks if the user is a Zywave user based on their account type.
 * @param {UserInfo} userInfo - The user information to check.
 * @returns {boolean} - Returns true if the user is a Zywave user, false otherwise.
 */
export const isZywaveUser = (userInfo: any) => {
  return userInfo?.account_type === 7;
};
